import React, { useState } from 'react';
import styles from './consult-with-sales.module.scss';
import {
    BasicDropdownMenu,
    Button,
    Dropdown,
    FlexContainer,
    MenuText,
    TextAreaField,
    UnifiedSelectBox,
} from '@placer-ui/components';
import { useAppUiActions } from 'store/app-ui/use-app-ui-actions';
import { UPGRADE_PLAN_SCREEN } from 'extensions/upgrade-your-plan-dialog/upgrade-your-plan-dialog';
import { sendConsultWithSalesForm } from 'extensions/upgrade-your-plan-dialog/upgrade-your-plan-dialog-accessible/utils/send-consult-with-sales-form';
import { useSplunkCallback } from 'hooks/use-splunk-callback/use-splunk-callback';
import { useSegmentCallback } from 'hooks/use-segment-callback/use-segment-callback';
import { useSelector } from 'react-redux';
import { userSelector } from 'store/selectors/app-selectors';

const TOPIC_ITEMS: Record<string, { value: string; label: string }> = {
    'want-purchase-full-report': {
        value: 'want-purchase-full-report',
        label: "I'd like to purchase the full report",
    },
    'want-upgrade-plan': {
        value: 'want-upgrade-plan',
        label: "I'd like to upgrade my plan",
    },
    'want-to-learn-more': {
        value: 'want-to-learn-more',
        label: "I'd like to learn more about the Placer platform",
    },
    other: {
        value: 'other',
        label: 'Other',
    },
};

type ConsultWithSalesFormProps = {
    onSubmit: () => void;
};

export const ConsultWithSalesForm = ({ onSubmit }: ConsultWithSalesFormProps) => {
    const [selectedTopic, setSelectedTopic] = useState('');
    const [otherTopicDescription, setOtherTopicDescription] = useState('');
    const user = useSelector(userSelector);
    const { openUpgradePlanPopup, closeUpgradePlanPopup } = useAppUiActions();
    const sendEvent = useSplunkCallback();
    const { track } = useSegmentCallback();

    const isOtherTopicSelected = selectedTopic === TOPIC_ITEMS.other.value;
    const isSubmitButtonDisabled =
        !selectedTopic || (isOtherTopicSelected && !otherTopicDescription);

    const handleSubmit = async () => {
        const selectedLabel = TOPIC_ITEMS[selectedTopic]?.label || '';

        await sendConsultWithSalesForm(
            user?.email || '',
            selectedLabel,
            otherTopicDescription.length ? otherTopicDescription : 'Not provided',
        );

        track({
            category: 'Popup',
            action: 'Submit upgrade',
        });

        sendEvent({
            component: 'contact-sales-popup',
            action: 'button-click',
            btn_name: 'submit-sales-request',
            value: selectedLabel,
        });

        closeUpgradePlanPopup();
        onSubmit();
    };

    return (
        <>
            <FlexContainer flexDirection={'column'} className={styles.description}>
                <MenuText variant={'semi-bold'}>Hey {user?.email},</MenuText>
                <MenuText variant={'semi-bold'}>
                    For more features please upgrade your plan
                </MenuText>
                <MenuText variant={'semi-bold'}>What would you like to talk about?</MenuText>
            </FlexContainer>
            <FlexContainer flexDirection={'column'} gap={'12px'} className={styles.select}>
                <Dropdown
                    trigger={
                        <UnifiedSelectBox
                            size={'large'}
                            placeholder={'Select an option'}
                            label={'Topic'}
                            value={TOPIC_ITEMS[selectedTopic]?.label || ''}
                            testId={'topic-selection-trigger'}
                        />
                    }
                    appendToParent
                    menuWidth={400}
                    closeOnSelection
                >
                    <BasicDropdownMenu
                        items={Object.values(TOPIC_ITEMS)}
                        onItemSelection={(item) => {
                            setSelectedTopic(item.value);
                        }}
                    />
                </Dropdown>
                {isOtherTopicSelected && (
                    <TextAreaField
                        label={'Tell us more'}
                        placeholder={'Please write what you like to talk about'}
                        testId={'tell-us-more-field'}
                        value={otherTopicDescription}
                        maxHeight={105}
                        maxWidth={400}
                        initialWidth={400}
                        initialHeight={105}
                        onChange={(value) => setOtherTopicDescription(value)}
                    />
                )}
            </FlexContainer>
            <FlexContainer
                flexDirection={'column'}
                alignItems={'center'}
                gap={'12px'}
                className={styles.actions}
            >
                <Button
                    fullWidth
                    variant={'primary-brand'}
                    testId={'submit-consult-with-sales-button'}
                    size={'large'}
                    disabled={isSubmitButtonDisabled}
                    onClick={handleSubmit}
                >
                    Done
                </Button>
                <Button
                    fullWidth
                    variant={'text'}
                    testId={'close-consult-with-sales-button'}
                    size={'large'}
                    onClick={() => openUpgradePlanPopup(UPGRADE_PLAN_SCREEN.default)}
                >
                    Maybe Later
                </Button>
            </FlexContainer>
        </>
    );
};
